import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Edit2, Trash2, Calendar, Eye, X } from 'lucide-react';
import bannerService from '../services/bannerService';
import { getImageUrl } from '../utils/imageUrl';
import './BannerManagement.css';

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const BannerManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(() => ['add', 'edit'].includes(searchParams.get('action')));
  const [editingBanner, setEditingBanner] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBannerId, setDeleteBannerId] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: null,
    imagePreview: null,
    external_url: '',
    is_active: true,
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  useEffect(() => {
    if (searchParams.get('action') === 'edit' && !editingBanner && banners.length) {
      const banner = banners.find((item) => item.id === Number(searchParams.get('id')));
      if (banner) handleEdit(banner);
    }
  }, [banners]);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await bannerService.getAll();
      if (response.success) {
        setBanners(response.data.banners);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      setError(error.message || 'Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));

      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({
            ...prev,
            imagePreview: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Title validation (required, 2-100 characters)
    if (!formData.title.trim()) {
      errors.title = 'Banner title is required';
    } else if (formData.title.trim().length < 2) {
      errors.title = 'Banner title must be at least 2 characters';
    } else if (formData.title.trim().length > 100) {
      errors.title = 'Banner title must not exceed 100 characters';
    }

    // Image validation (optional)
    if (formData.image && formData.image.size > 5 * 1024 * 1024) {
      errors.image = 'Image size must be less than 5MB';
    }

    // Link validation
    if (formData.external_url && !isValidUrl(formData.external_url)) {
      errors.external_url = 'Please enter a valid URL';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate form before submission
    if (!validateForm()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key !== 'imagePreview' && formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      if (editingBanner) {
        const response = await bannerService.update(editingBanner.id, submitData);
        if (response.success) {
          setSuccess('Banner updated successfully');
          fetchBanners();
          resetForm();
        }
      } else {
        const response = await bannerService.create(submitData);
        if (response.success) {
          setSuccess('Banner created successfully');
          fetchBanners();
          resetForm();
        }
      }
    } catch (error) {
      setError(error.message || 'Failed to save banner');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      description: banner.description || '',
      image: null,
      imagePreview: banner.image_url,
      external_url: banner.external_url || '',
      is_active: banner.is_active,
      start_date: banner.start_date ? banner.start_date.split('T')[0] : '',
      end_date: banner.end_date ? banner.end_date.split('T')[0] : ''
    });
    setShowForm(true);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('action', 'edit');
      next.set('id', String(banner.id));
      return next;
    }, { replace: true });
  };

  const handleDeleteClick = (id) => {
    setDeleteBannerId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      const response = await bannerService.delete(deleteBannerId);
      if (response.success) {
        setSuccess('Banner deleted successfully');
        fetchBanners();
        setDeleteDialogOpen(false);
        setDeleteBannerId(null);
      }
    } catch (error) {
      setError(error.message || 'Failed to delete banner');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id) => {
    setLoading(true);
    try {
      await bannerService.toggleActive(id);
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
      setError(error.message || 'Failed to toggle banner');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingBanner(null);
    setFormData({ title: '', description: '', image: null, imagePreview: null, external_url: '', is_active: true, start_date: '', end_date: '' });
    setValidationErrors({});
    setError('');
    setShowForm(true);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('action', 'add');
      next.delete('id');
      return next;
    }, { replace: true });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image: null,
      imagePreview: null,
      external_url: '',
      is_active: true,
      start_date: '',
      end_date: ''
    });
    setEditingBanner(null);
    setShowForm(false);
    setValidationErrors({});
    setError('');
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('action');
      next.delete('id');
      return next;
    }, { replace: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="category-management-container">
      {/* Header */}
      <div className="cm-header">
        <div>
          <h1 className="cm-title">Banner Management</h1>
          <p className="cm-subtitle">Manage promotional banners for the mobile app</p>
        </div>
        <Button className="cm-add-btn" onClick={handleAdd}>
          + Add Banner
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Add/edit modal */}
      {showForm && (
        <div className="modal-overlay-new" onClick={resetForm}>
          <div className="modal-content-new banner-form-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header-new">
              <h2 className="modal-title-new">{editingBanner ? 'Edit Banner' : 'Add Banner'}</h2>
              <button type="button" className="modal-close-new" onClick={resetForm} aria-label="Close"><X size={16} /></button>
            </div>
            <div className="modal-body-new">
            <form onSubmit={handleSubmit} className="banner-form">
              <div className="form-group">
                <Label>Banner Title <span className="required">*</span></Label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter banner title"
                />
                {validationErrors.title && (
                  <span className="error-message">{validationErrors.title}</span>
                )}
              </div>

              <div className="form-group">
                <Label>Description</Label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Enter banner description (optional)"
                  className="cm-textarea"
                />
              </div>

              <div className="form-group">
                <Label>Banner Image</Label>
                <div className="file-upload-area">
                  <div className="file-upload-content">
                    <div className="file-upload-icon">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17,8 12,3 7,8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                    </div>
                    <div className="file-upload-text">
                      <p className="file-upload-primary">Choose banner image or drag and drop</p>
                      <p className="file-upload-secondary">JPEG, JPG, PNG, GIF, WEBP (Max 5MB) - Recommended: 1200x400px</p>
                    </div>
                    <Button type="button" variant="outline" className="file-upload-button">
                      Browse Image
                    </Button>
                  </div>
                  <input
                    type="file"
                    name="image"
                    onChange={handleInputChange}
                    accept="image/*"
                    className="file-upload-input"
                  />
                </div>
                {validationErrors.image && (
                  <span className="error-message">{validationErrors.image}</span>
                )}
                {formData.imagePreview && (
                  <div className="image-preview">
                    <img src={getImageUrl(formData.imagePreview)} alt="Preview" className="preview-image" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <Label>External URL (Optional)</Label>
                <Input
                  type="url"
                  name="external_url"
                  value={formData.external_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
                {validationErrors.external_url && (
                  <span className="error-message">{validationErrors.external_url}</span>
                )}
                <p className="field-hint">Banner will open this URL when clicked</p>
              </div>

              <div className="bm-form-row">
                <div className="form-group">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="checkbox-container">
                  <Checkbox
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active" className="checkbox-label">Active</Label>
                </div>
              </div>

              <div className="cm-form-actions">
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : editingBanner ? 'Update Banner' : 'Create Banner'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Banner list remains visible behind the modal. */}
      <Card>
          <CardHeader>
            <CardTitle>Banner List</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>External URL</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && banners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="8" className="cm-table-empty">Loading...</TableCell>
                  </TableRow>
                ) : banners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="8" className="cm-table-empty">No banners found</TableCell>
                  </TableRow>
                ) : (
                  banners.map(banner => (
                    <TableRow key={banner.id}>
                      <TableCell className="cm-id-cell">{banner.id}</TableCell>
                      <TableCell>
                        {banner.image_url ? (
                          <img
                            src={getImageUrl(banner.image_url)}
                            alt={banner.title}
                            className="bm-banner-image"
                          />
                        ) : (
                          <div className="bm-banner-placeholder">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="9" cy="9" r="2" />
                              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                            </svg>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="bm-banner-title">{banner.title}</div>
                      </TableCell>
                      <TableCell>
                        <div className="bm-banner-description">
                          {banner.description ?
                            (banner.description.length > 50 ?
                              banner.description.substring(0, 50) + '...' :
                              banner.description
                            ) : '-'
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="bm-link-type">
                          {banner.external_url ? (
                            <a href={banner.external_url} target="_blank" rel="noopener noreferrer" className="bm-external-link icon-only-btn" title="Open external link" aria-label="Open external link">
                              <Eye size={15} />
                            </a>
                          ) : '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="bm-duration">
                          <Calendar size={14} />
                          <span>{formatDate(banner.start_date)} - {formatDate(banner.end_date)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={banner.is_active ? 'active' : 'inactive'}>
                          {banner.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="cm-actions">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(banner)}
                            className="cm-edit-btn"
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(banner.id)}
                            className="cm-delete-btn"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Banner</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this banner? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="cm-dialog-actions">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="cm-delete-btn">
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BannerManagement;
